import { authStore } from '../stores/authStore'

/**
 * usePermission — central permission hook for all CRM-portal UI components.
 *
 * Reads from authStore.user.permissionsMap (an O(1) lookup built from the
 * role_menu_action API response) and exposes two helpers:
 *
 *  - canViewMenu(path)          → controls Sidebar link visibility
 *  - hasAction(path, action)    → controls individual button / icon visibility
 *
 * The permissions map is the sole source of truth — role name (including
 * SUPER_ADMIN) never bypasses a check. If the map says no, access is denied.
 *
 * Usage:
 *   const { hasAction } = usePermission()
 *   {hasAction('/leads', 'delete') && <DeleteButton />}
 */



/**
 * Paths that must NOT inherit permissions from their parent path via the walk-up algorithm.
 * These require explicit role-menu-action mapping assignments in Keycloak / User Manager.
 */
const STRICT_PATHS = [
  '/settings/knowlarity',
]

export function usePermission() {
  const roleName     = authStore.user?.roleName ?? ''
  const isSuperAdmin = roleName === 'SUPER_ADMIN'
  const map          = authStore.user?.permissionsMap ?? {}

  /** Normalise to exact lower-case full path: "/leads/some-slug" */
  const normalizeExact = (path: string): string =>
    '/' + path.replace(/^\/|\/$/g, '').toLowerCase()

  /** Normalise to first-segment only: "/leads" */
  const normalizeFirst = (path: string): string => {
    const clean = path.replace(/^\/|\/$/g, '').toLowerCase()
    return '/' + clean.split('/')[0]
  }

  /**
   * Resolves the best ACL map key for a given path.
   *
   * Walk-up algorithm:
   * Start from the full normalized path (e.g. "/sales/customer-verification/cv-005")
   * and walk up the path segments to find the first parent/ancestor path that is
   * explicitly defined in the permissions map.
   *
   * If an ancestor is found, it is returned (access is inherited from parent).
   * If a sub-path is explicitly defined in the map, its own value is used (no inheritance).
   * Stops before falling back to root "/" to prevent global access leak.
   */
  const resolveKey = (path: string): string | undefined => {
    const exact = normalizeExact(path)

    // Strict paths do not inherit from parent segments
    if (STRICT_PATHS.includes(exact)) {
      return map[exact] !== undefined ? exact : undefined
    }

    // 1. Exact match wins immediately
    if (map[exact] !== undefined) return exact

    // 2. Walk up parent segments
    let currentPath = exact
    while (currentPath && currentPath !== '/') {
      if (map[currentPath] !== undefined) {
        return currentPath
      }
      // Strip trailing segment
      const lastSlashIdx = currentPath.lastIndexOf('/')
      if (lastSlashIdx <= 0) {
        break
      }
      currentPath = currentPath.substring(0, lastSlashIdx)
    }

    return undefined
  }

  /**
   * Returns true when the user's role is allowed to visit a menu path at all.
   * A menu is considered visible if ANY of its actions has isChecked === true.
   * Permission map is the sole source of truth — no role-name shortcuts.
   */
  const canViewMenu = (path: string): boolean => {
    const key = resolveKey(path)
    if (!key) return false
    return Object.values(map[key]).some((v) => v === true)
  }

  /**
   * Returns true when the user's role has a specific action checked for a path.
   * Permission map is the sole source of truth — no role-name shortcuts.
   */
  const hasAction = (path: string, action: string): boolean => {
    const key = resolveKey(path)
    if (!key) return false
    return map[key]?.[action.toLowerCase()] === true
  }

  return { canViewMenu, hasAction, isSuperAdmin }
}
